import { Pie } from '@ant-design/charts';
import { PieChartOutlined } from '@ant-design/icons';
import { Card, Empty } from 'antd';
import { useTranslation } from 'react-i18next';

import { TurnCountByTaskData } from '../types';

const BASE = 'scenes.kit.dashboard.components.dashboard_donut_chart';

interface DashboardDonutChartProps {
  data?: TurnCountByTaskData[];
  loading?: boolean;
}

const COLORS = [
  '#00c0b3',
  '#36cfc9',
  '#5cdbd3',
  '#87e8de',
  '#b5f5ec',
  '#00a89c',
  '#007a71',
  '#004d49',
];

function DashboardDonutChart({ data, loading }: DashboardDonutChartProps) {
  const { t } = useTranslation();
  const isEmpty = !data || data.length === 0;

  const chartData = isEmpty
    ? []
    : data
        .map((item) => ({
          type: item.taskName,
          turnos: item.turnCount,
        }))
        .sort((a, b) => a.turnos - b.turnos);

  const total = data?.reduce((s, d) => s + d.turnCount, 0) || 0;

  const config = {
    data: chartData,
    angleField: 'turnos',
    colorField: 'type',
    radius: 1,
    innerRadius: 0.6,
    color: COLORS,
    legend: {
      position: 'bottom' as const,
    },
    label: {
      text: (d: any) => {
        const percent = total > 0 ? ((d.turnos / total) * 100).toFixed(0) : 0;
        return `${percent}%`;
      },
      style: {
        fill: '#ffffff',
        fontSize: 12,
        fontWeight: 'bold',
      },
      position: 'inside' as const,
    },
    tooltip: {
      title: 'type',
      items: [
        (d: any) => {
          const percent = total > 0 ? ((d.turnos / total) * 100).toFixed(0) : 0;
          return {
            name: t(`${BASE}.tooltip.shifts`),
            value: `${d.turnos} (${percent}%)`,
          };
        },
      ],
    },
    statistic: {
      title: false,
      content: {
        style: {
          whiteSpace: 'pre-wrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontSize: 22,
          fontWeight: 'bold',
        },
        content: isEmpty ? '0' : `${total}`,
      },
    },
    interactions: [{ type: 'element-active' }],
  };

  const cardTitle = t(`${BASE}.title`);

  return (
    <Card title={cardTitle} styles={{ body: { paddingTop: 12 } }}>
      <div
        style={{
          fontSize: 12,
          color: '#8c8c8c',
          marginBottom: 12,
        }}
      >
        {t(`${BASE}.subtitle`)}
      </div>
      {loading ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 300,
          }}
        >
          <div className="kit-dashboard__chart-skeleton-donut" />
        </div>
      ) : isEmpty ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 300,
          }}
        >
          <Empty
            description={
              <span style={{ color: '#d9d9d9' }}>{t(`${BASE}.empty`)}</span>
            }
            image={
              <PieChartOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
            }
            imageStyle={{ height: 48, marginBottom: 4 }}
          />
        </div>
      ) : (
        <Pie {...config} height={300} />
      )}
    </Card>
  );
}

export default DashboardDonutChart;
